import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// Components
import Logo from '../../components/Logo';

// Actions and utilities
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import { handleErrors, showToaster } from '../../components/Common/common';
import { PAGEPATHS } from 'components/Common/Constant';

const ForgetPasswordPage = () => {
  document.title = "Forgot Password | GOVCOM - Government Compliance Dashboard";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
    }),
    onSubmit: (values) => {
      handleSubmit(values);
    }
  });

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const postForm = {
        email: values.email
      };

      const { data } = await axiosInstance.post(ApiNames.USER_FORGOTPASS, postForm);
      if (data) {
        showToaster(data.status, data.message);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 to-purple-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center space-x-3 text-white">
            {/* <Logo size={50} variant="white" checkColor="#3b82f6" />
            <span className="text-3xl font-bold">GOVCOM</span> */}
            <img
              src="/images/Logo_low_white.png"
              alt="GOVCOM Logo"
              className="h-12 w-auto"
            />
          </Link>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white mb-6">
            Forgot Your Password?
          </h1>
          <p className="text-indigo-100 text-lg leading-relaxed max-w-md">
            No worries! Enter your email address and we'll send you instructions to reset your password.
          </p>

          <div className="mt-12 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-indigo-100">Receive password reset link via email</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <p className="text-indigo-100">Secure and encrypted password reset</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-indigo-100">Reset link expires after 24 hours</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-indigo-100 text-sm">
          © {new Date().getFullYear()} GOVCOM. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Forgot Password Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <Link to="/" className="lg:hidden flex items-center space-x-2 text-blue-600 mb-8">
              {/* <Logo size={42} />
              <span className="text-2xl font-bold">GOVCOM</span> */}

              <img
                src="/images/Logo_low.png"
                alt="GOVCOM Logo"
                className="h-12 w-auto"
              />
            </Link>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Reset your password
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Remember your password?{' '}
              <Link to={PAGEPATHS.Login} className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>

          <div className="mt-8">
            <form onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={validation.values.email || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.email && validation.errors.email
                        ? 'border-red-300'
                        : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="you@example.com"
                  />
                </div>
                {validation.touched.email && validation.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.email}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending reset link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              {/* Additional Help */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Need help? Contact{' '}
                  <a href="mailto:support@govcompliance.com" className="font-medium text-blue-600 hover:text-blue-500">
                    support@govcompliance.com
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPasswordPage;
